import React, { Fragment, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Add,
  Group,
  CheckCircle,
  Cancel,
  Search,
  Visibility,
} from "@mui/icons-material";
import { IconButton } from "@mui/material";
import { memberHeader } from "../utils/HeaderData";
import CustomTable from "../templates/CustomTable";
import {
  collection,
  getDocs,
  orderBy,
  query,
  doc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import { toast } from "react-toastify";
import { useUser } from "../components/context/UserContext";
import { MemberPreviewModal } from "../components/Members/MemberPreviewModal";

const Members = () => {
  const navigate = useNavigate();
  const { userRole } = useUser();
  const [members, setMembers] = useState([]);
  const [previewMember, setPreviewMember] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const fetchMembers = async () => {
    try {
      const [membersSnap, familiesSnap] = await Promise.all([
        getDocs(query(collection(db, "members"), orderBy("createdAt", "desc"))),
        getDocs(collection(db, "families")),
      ]);
      const familyNameById = {};
      familiesSnap.docs.forEach((d) => {
        familyNameById[d.id] = d.data().familyName || "";
      });
      setMembers(
        membersSnap.docs.map((d) => ({
          _id: d.id,
          ...d.data(),
          familyName: familyNameById[d.data().familyId] || "",
          createdAt: d.data().createdAt?.toDate() || new Date(),
        })),
      );
    } catch (err) {
      console.error(err.message);
    }
  };

  React.useEffect(() => {
    fetchMembers();
  }, []);

  const handleStatusChange = async (id, status) => {
    try {
      await updateDoc(doc(db, "members", id), { status });
      setMembers((prev) =>
        prev.map((m) => (m._id === id ? { ...m, status } : m)),
      );
      toast(status === "approved" ? "Member approved" : "Member declined", {
        type: status === "approved" ? "success" : "warning",
      });
    } catch (err) {
      console.error(err);
      toast("Failed to update status", { type: "error" });
    }
  };

  const handlePreview = (row) => {
    setPreviewMember(row);
    setPreviewOpen(true);
  };

  const canApprove = userRole === "admin" || userRole === "superadmin";
  const canManage = userRole !== "operator";

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const STATUS_FILTERS = [
    { key: "all", label: "All" },
    { key: "approved", label: "Approved" },
    { key: "pending", label: "Pending" },
    { key: "declined", label: "Declined" },
  ];

  const statusCounts = {
    all: members.length,
    approved: members.filter((m) => m.status === "approved").length,
    pending: members.filter((m) => m.status === "pending").length,
    declined: members.filter((m) => m.status === "declined").length,
  };

  const filteredMembers = members.filter((m) => {
    if (statusFilter !== "all" && m.status !== statusFilter) return false;
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    const dept = Array.isArray(m.department)
      ? m.department.join(", ")
      : m.department || "";
    return (
      (m.name || "").toLowerCase().includes(q) ||
      dept.toLowerCase().includes(q) ||
      (m.dob || "").toLowerCase().includes(q) ||
      (m.gender || "").toLowerCase().includes(q)
    );
  });

  const extraActions = (row) => (
    <div className="flex gap-1 flex-wrap mt-0.5 items-center">
      <IconButton
        size="small"
        onClick={() => handlePreview(row)}
        sx={{ color: "#000C66", "&:hover": { backgroundColor: "#eef2ff" } }}
      >
        <Visibility fontSize="small" />
      </IconButton>
      {canApprove && row.status !== "approved" && (
        <button
          onClick={() => handleStatusChange(row._id, "approved")}
          className="flex items-center gap-0.5 text-xs px-2 py-1 bg-green-50 text-green-700 border border-green-200 rounded-lg font-abel hover:bg-green-100 transition"
        >
          <CheckCircle style={{ fontSize: 13 }} /> Approve
        </button>
      )}
      {canApprove && row.status !== "approved" && row.status !== "declined" && (
        <button
          onClick={() => handleStatusChange(row._id, "declined")}
          className="flex items-center gap-0.5 text-xs px-2 py-1 bg-red-50 text-red-600 border border-red-200 rounded-lg font-abel hover:bg-red-100 transition"
        >
          <Cancel style={{ fontSize: 13 }} /> Decline
        </button>
      )}
    </div>
  );

  return (
    <Fragment>
      <div className="min-h-screen bg-[#f5f7fe] p-3 sm:p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#ecfeff] flex items-center justify-center flex-shrink-0">
                <Group style={{ color: "#0891b2", fontSize: 20 }} />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-[#000C66] font-aref">
                  Members
                </h1>
                <p className="text-gray-400 text-xs font-abel">
                  Manage church members directory
                  {canApprove && (
                    <span className="ml-2 text-[#000C66] font-semibold">
                      · Approver access enabled
                    </span>
                  )}
                </p>
              </div>
            </div>
            {canManage && (
              <button
                onClick={() => navigate("/create-member")}
                className="flex items-center justify-center gap-2 bg-[#000C66] text-white px-5 py-2.5 rounded-xl hover:bg-blue-900 transition font-abel text-sm font-semibold w-full sm:w-auto"
              >
                <Add style={{ fontSize: 18 }} /> Add Member
              </button>
            )}
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-4 border-b border-gray-100 flex flex-col gap-3">
              <div className="flex items-center gap-2 flex-wrap">
                {STATUS_FILTERS.map((f) => {
                  const active = statusFilter === f.key;
                  return (
                    <button
                      key={f.key}
                      onClick={() => setStatusFilter(f.key)}
                      className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-abel font-semibold border transition ${
                        active
                          ? "bg-[#000C66] text-white border-[#000C66]"
                          : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50"
                      }`}
                    >
                      {f.label}
                      <span
                        className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                          active
                            ? "bg-white/20 text-white"
                            : "bg-gray-100 text-gray-500"
                        }`}
                      >
                        {statusCounts[f.key] ?? 0}
                      </span>
                    </button>
                  );
                })}
              </div>
              <div className="relative max-w-sm">
                <Search
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  style={{ fontSize: 18 }}
                />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name, department, DOB, gender…"
                  className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm font-abel text-gray-700 focus:outline-none focus:border-[#000C66] focus:ring-2 focus:ring-[#000C66]/10 transition bg-gray-50"
                />
              </div>
            </div>
            <CustomTable
              headers={memberHeader}
              data={filteredMembers}
              editPath={canManage ? "/update-member" : null}
              deletePath={canManage ? "/delete-member" : null}
              extraActions={extraActions}
              resetKey={statusFilter}
            />
          </div>
        </div>
      </div>

      <MemberPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        member={previewMember}
        familyName={previewMember?.familyName}
        canViewPhone={canApprove}
        canMakeAuthor={canApprove}
      />
    </Fragment>
  );
};

export default Members;
